// src/services/privacy-consent.service.ts
import {
  CONSENT_STORAGE_KEY,
  LEGAL_AGREEMENT_STORAGE_KEY,
  REQUIRED_LEGAL_VERSION,
} from "../legal/legal.constants";
import type { ConsentPreferences, StoredConsent, LegalAgreementRecord } from "../legal/legal.types";

const CONSENT_VERSION = "2026-05-10";

const DEFAULT_CONSENT: ConsentPreferences = {
  essential: true,
  functional: true,
  analytics: false,
  marketing: false,
};

class PrivacyConsentService {
  getConsent(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as StoredConsent;

      if (!parsed?.preferences?.essential) return null;
      if (parsed.version !== CONSENT_VERSION) return null;

      return parsed;
    } catch {
      return null;
    }
  }

  hasMadeChoice(): boolean {
    return Boolean(this.getConsent());
  }

  getPreferences(): ConsentPreferences {
    return this.getConsent()?.preferences || DEFAULT_CONSENT;
  }

  saveConsent(preferences: ConsentPreferences): StoredConsent {
    const existing = this.getConsent();

    const record: StoredConsent = {
      version: CONSENT_VERSION,
      preferences: {
        essential: true,
        functional: Boolean(preferences.functional),
        analytics: Boolean(preferences.analytics),
        marketing: Boolean(preferences.marketing),
      },
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    window.dispatchEvent(new CustomEvent("lrnsy:privacy-consent-updated", { detail: record }));

    return record;
  }

  acceptEssentialOnly(): StoredConsent {
    return this.saveConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  }

  acceptRecommended(): StoredConsent {
    return this.saveConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: false,
    });
  }

  acceptAll(): StoredConsent {
    return this.saveConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  }

  canUseFunctionalStorage(): boolean {
    return this.getPreferences().functional;
  }

  canUseAnalytics(): boolean {
    return this.getPreferences().analytics;
  }

  canUseMarketing(): boolean {
    return this.getPreferences().marketing;
  }

  saveLegalAgreement(): LegalAgreementRecord {
    const record: LegalAgreementRecord = {
      accepted: true,
      termsVersion: REQUIRED_LEGAL_VERSION.termsVersion,
      privacyVersion: REQUIRED_LEGAL_VERSION.privacyVersion,
      cookiesVersion: REQUIRED_LEGAL_VERSION.cookiesVersion,
      acceptedAt: new Date().toISOString(),
    };

    localStorage.setItem(LEGAL_AGREEMENT_STORAGE_KEY, JSON.stringify(record));
    return record;
  }

  getLegalAgreement(): LegalAgreementRecord | null {
    try {
      const raw = localStorage.getItem(LEGAL_AGREEMENT_STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as LegalAgreementRecord;

      if (!parsed.accepted) return null;
      if (parsed.termsVersion !== REQUIRED_LEGAL_VERSION.termsVersion) return null;
      if (parsed.privacyVersion !== REQUIRED_LEGAL_VERSION.privacyVersion) return null;
      if (parsed.cookiesVersion !== REQUIRED_LEGAL_VERSION.cookiesVersion) return null;

      return parsed;
    } catch {
      return null;
    }
  }

  hasAcceptedCurrentLegalTerms(): boolean {
    return Boolean(this.getLegalAgreement());
  }

  clearLocalDrafts(): void {
    const keysToRemove = ["lrnsy_local_create_draft", "lrnsy_pending_finalize_after_auth"];

    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // Silent by design.
      }
    });

    window.dispatchEvent(new CustomEvent("lrnsy:local-drafts-cleared"));
  }

  clearConsentAndPreferences(): void {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      localStorage.removeItem(LEGAL_AGREEMENT_STORAGE_KEY);
    } catch {
      // Silent by design.
    }

    window.dispatchEvent(new CustomEvent("lrnsy:privacy-consent-cleared"));
  }
}

export const privacyConsentService = new PrivacyConsentService();
